const DEFAULT_RANGE_YEARS = 9;
const MAXIMUM_RANGE_DAYS = 3660;
const DAY_MILLISECONDS = 86_400_000;

export class FundamentalEventDataSource {
  constructor(endpoint = "/api/v1/chart-events") {
    this.endpoint = endpoint.replace(/\/$/, "");
  }

  async load({ symbolId, from, to, includeUnverified = false, signal }) {
    if (!symbolId) {
      return {
        symbolId: null,
        events: [],
        coverage: [],
        state: "Unsupported",
        message: "This saved chart does not yet have a canonical SymbolService ID."
      };
    }
    const ranges = fundamentalEventRequestRanges(from, to);
    if (ranges.length > 1) {
      const feeds = await Promise.all(ranges.map(range => this.load({
        symbolId, ...range, includeUnverified, signal
      })));
      const events = [...new Map(feeds.flatMap(feed => feed.events || [])
        .map(event => [event.id, event])).values()]
        .sort((left, right) => String(left.date).localeCompare(String(right.date)) ||
          String(left.id).localeCompare(String(right.id)));
      const states = new Set(feeds.map(feed => feed.state));
      const state = states.has("Stale") ? "Stale"
        : states.size === 1 ? feeds[0].state
        : states.has("Unavailable") || states.has("Partial") ? "Partial"
        : events.length > 0 ? "Available" : "Empty";
      return { ...feeds[0], from, to, events, state };
    }
    const query = new URLSearchParams({
      from,
      to,
      includeUnverified: String(includeUnverified)
    });
    const response = await fetch(
      `${this.endpoint}/${encodeURIComponent(symbolId)}?${query}`,
      {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal
      }
    );
    if (!response.ok) {
      const error = new Error(`Fundamental-event request failed with status ${response.status}.`);
      error.name = "FundamentalEventRequestError";
      error.status = response.status;
      throw error;
    }
    return response.json();
  }
}

export function fundamentalEventDateRange(now = new Date(), historyRange = null) {
  const to = new Date(Date.UTC(
    now.getUTCFullYear() + 1,
    now.getUTCMonth(),
    now.getUTCDate()
  ));
  const from = new Date(Date.UTC(
    now.getUTCFullYear() - DEFAULT_RANGE_YEARS,
    now.getUTCMonth(),
    now.getUTCDate()
  ));
  return {
    from: historyRange?.start && historyRange.start < from.toISOString().slice(0, 10)
      ? historyRange.start : from.toISOString().slice(0, 10),
    to: historyRange?.end && historyRange.end > to.toISOString().slice(0, 10)
      ? historyRange.end : to.toISOString().slice(0, 10)
  };
}

export function fundamentalEventRequestRanges(from, to) {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [{ from, to }];
  const ranges = [];
  for (let cursor = start; cursor <= end;) {
    const chunkEnd = Math.min(end, cursor + MAXIMUM_RANGE_DAYS * DAY_MILLISECONDS);
    ranges.push({
      from: new Date(cursor).toISOString().slice(0, 10),
      to: new Date(chunkEnd).toISOString().slice(0, 10)
    });
    cursor = chunkEnd + DAY_MILLISECONDS;
  }
  return ranges;
}
